import { Component, EventEmitter, HostListener, OnDestroy, Output } from "@angular/core";
import { Contract } from "@edp-components/edp-contract";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { EdpToastConfig, EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { SCREEN_SIZES } from "@edp-enums/screen-sizes.enum";
import { SelfcareEnum } from "@edp-enums/selfcare.enum";
import { RequestsAndOperations, RequestsAndOperationsFactory } from "@edp-models/requests-and-operations";
import { INewRequestAndOperation, NewRequestService } from "@edp-pages/requests-and-operations/services/new-request.service";
import { DatalayerService } from "@edp-services/datalayer.service";
import { SetNewRequest, SetNewRequestStatus, SetSelectedServiceAndMotive } from "@edp-store/actions/requests-and-operations.actions";
import { getContract } from "@edp-store/selectors/contract.selectors";
import { getNewRequestStatus, getSelectedServiceAndMotive } from "@edp-store/selectors/requests-and-operations.selector";
import { IAppState } from "@edp-store/state/app.state";
import { select, Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { combineLatest, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";

@Component({
    selector: "create-request-second-step",
    templateUrl: "./create-request-second-step.component.html",
    styleUrls: ["./create-request-second-step.component.scss"]
})

export class CreateRequestSecondStep implements OnDestroy {
    @Output() closeModal = new EventEmitter();
    @Output() backStep = new EventEmitter();

    public request$ = this.newRequestService.getRequestAndService();
    private unsubscribe$ = new Subject();
    public selectedContract: Contract;
    public selectedRequest: INewRequestAndOperation;
    public selfcareEnum = SelfcareEnum;
    public showForm = false;
    public isMobile = false;
    private datalayerSetted = false;

    @HostListener("window:resize", ["$event"])
    onResize(event) {
        this.isMobile = window.innerWidth <= SCREEN_SIZES.WDGT;
    }

    constructor(
        private readonly newRequestService: NewRequestService,
        private readonly store: Store<IAppState>,
        private readonly translateService: TranslateService,
        private readonly toaster: EdpToasterService,
        private datalayerService: DatalayerService
    ) {

        combineLatest([
            this.store.pipe(select(getContract)),
            this.store.pipe(select(getSelectedServiceAndMotive)),
        ]).subscribe(([contract, newRequest]) => {
            this.selectedContract = contract;
            this.selectedRequest = newRequest;

            if (!this.datalayerSetted) {
                this.datalayerSetted = true;
                !!newRequest && newRequest.selectedMotive.externalCategory !== "02170300"
                    ? this.datalayerService.setGaClick("requestsNewModalNext", {
                          serviceAttributes: { motivo: newRequest.selectedMotive.name },
                      })
                    : this.datalayerService.setGaClick("requestsNewModalNext");

                !!newRequest && newRequest.selectedMotive.externalCategory === "02170300"
                    ? this.datalayerService.setGaEvent("requestsNewModalStep2", {
                          serviceAttributes: { motivo: newRequest.selectedMotive.name },
                      })
                    : this.datalayerService.setGaEvent("requestsNewModalStep2");
            }
        });

        this.store.pipe(select(getNewRequestStatus), takeUntil(this.unsubscribe$)).subscribe((newRequesStatus: any) => {
            if (newRequesStatus) {
                if (newRequesStatus.success) {
                    const isFunciona = newRequesStatus.externalCategory && newRequesStatus.externalCategory === "02030100";
                    const successMessage = isFunciona
                        ? "pt.edp.edponline.requests-and-operations.create-request.submit_request_message_funciona"
                        : "pt.edp.edponline.requests-and-operations.create-request.submit_request_message";
                    const toasterConfig = isFunciona ? { timeout: 5000 } : { timeout: -1 };

                    this.datalayerRequestSuccess(successMessage);

                    this.showToaster(EdpToastType.success, successMessage, toasterConfig);
                } else {
                    this.datalayerRequestError();
                    this.showToaster(EdpToastType.error, "pt.edp.edponline.requests-and-operations.create-request.submit_request_error_message");
                }

                this.store.dispatch(new SetNewRequestStatus(null));

                this.closeModal.emit();
            }
        });

        this.isMobile = window.innerWidth <= SCREEN_SIZES.WDGT;
    }

    ngOnDestroy(): void {
        this.unsubscribe$.next();
        this.unsubscribe$.complete();
        this.store.dispatch(new SetNewRequestStatus(null));
        this.store.dispatch(new SetSelectedServiceAndMotive(null));
    }

    private datalayerRequestSuccess(successMessage: string): void {
        this.datalayerService.setGaEvent("requestsNewModalStep3", {
            serviceAttributes: {
                mensagemResultado: this.translateService.instant(successMessage),
            },
        });
    }

    private datalayerRequestError(): void {
        this.datalayerService.setGaEvent("requestsNewModalStep3", {
            formStepName: "erro",
            serviceAttributes: {
                mensagemResultado: this.translateService.instant(
                    "pt.edp.edponline.requests-and-operations.create-request.submit_request_error_message"
                ),
            },
        });
    }

    public submit(formData) {
        const message = this.getCompleteMessage(formData.description, formData.data);
        const newRequest = RequestsAndOperationsFactory.create({
            ContractID: this.selectedContract.ContractId,
            Description: message,
            Motive: this.selectedRequest.selectedMotive.name,
            ExternalCategory: this.selectedRequest.selectedMotive.externalCategory,
            ID: Number(this.selectedRequest.selectedMotive.id),
            ServiceAcronym: this.selectedRequest.selectedService.acronym,
            Subject: this.selectedRequest.selectedService.name,
            Attachments: this.getFormattedFiles(formData.files),
        } as RequestsAndOperations);

        newRequest.captchaToken = formData.recaptchaToken;

        this.datalayerService.setGaClick("requestsNewModalNext");

        this.store.dispatch(new SetNewRequest({
            contract: this.selectedContract,
            request: newRequest
        }));
    }

    private getCompleteMessage(description, data) {
        switch (this.selectedRequest.selectedMotive.externalCategory) {
            case "02020600": // TERMINAR CONTRATO
                return this.rescissionRequestMessage(description, data);
            case "02170300": // ELIMINAR CONTA
                return this.deleteRequestMessage(description, data.type);
            default:
                return description;
        }
    }

    private deleteRequestMessage(description, motiveLabel) {
        return `${!!description ? description + "\n" : ""}${motiveLabel}`;
    }

    private rescissionRequestMessage(description, data) {
        let message = (!!description ? `${description}\n` : "")
        + `Data de Rescisão: ${data.date} \nHorário de Rescisão: ${data.period}`;

        if (!!data.type) {
            message += `\n${this.translateService.instant("pt.edp.edponline.request.operations.contract." + data.type)}`;
        }

        return message;
    }

    private getFormattedFiles(files) {
        const newFiles = [];

        files.forEach(el => {
            const element: { DocData, MimeType, Id; ArchObj?, ContentRep?, AdditionalObjects?, } = {
                DocData: el.name,
                MimeType: el.mimeType,
                Id: el.id,
            };
            if (el.ArchObj) {
                // fields Certificação Gás + Autodeclaração
                element.ArchObj = el.ArchObj;
                element.ContentRep = el.ContentRep;
                element.AdditionalObjects = el.AdditionalObjects;
            }
            newFiles.push(element);
        });

        return newFiles;
    }

    private showToaster(type: EdpToastType, message: string, config?: EdpToastConfig): void {
        this.toaster.create(this.translateService.instant(message), type, config).show();
    }

    public goBack() {
        this.backStep.emit();
    }
}
